import { logger } from "@formant/core/common/logger";
import { ICommandStore } from "@formant/core/model/data/ICommandStore";
import { IDeviceStore } from "@formant/core/model/data/IDeviceStore";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { IModuleStore } from "@formant/core/model/data/IModuleStore";
import { IQueryStore } from "@formant/core/model/data/IQueryStore";

import {
    adapterClient,
    analyticsClient,
    analyticsModuleClient,
    annotationClient,
    annotationTemplateClient,
    authClient,
    channelClient,
    commandClient,
    commandTemplateClient,
    commentClient,
    countClient,
    deviceClient,
    deviceConfigurationTemplateClient,
    deviceDetailsClient,
    documentRagServiceClient,
    eventClient,
    eventTriggerClient,
    fileClient,
    groupClient,
    metadataClient,
    moduleClient,
    moduleConfigurationClient,
    onlineDevicesClient,
    organizationClient,
    presenceClient,
    queryClient,
    roleClient,
    rtcSignalingClient,
    scheduleClient,
    shareClient,
    signalingV2Client,
    ssoClient,
    streamClient,
    streamCurrentValueClient,
    tagTemplateClient,
    teamClient,
    usageMetricsClient,
    userClient,
    viewClient,
} from "./clients";
import { adminApi, ingestionApi, queryApi } from "./config";
import { AdapterStore } from "./store/AdapterStore";
import { AnalyticsModuleStore } from "./store/AnalyticsModuleStore";
import { AnalyticsStore } from "./store/AnalyticsStore";
import { AnnotationStore } from "./store/AnnotationStore";
import { AnnotationTemplateStore } from "./store/AnnotationTemplateStore";
import { AppStore } from "./store/AppStore";
import { AuthStore } from "./store/AuthStore";
import { BenchmarkStore } from "./store/BenchmarkStore";
import { ChannelStore } from "./store/ChannelStore";
import { CloudFileStore } from "./store/CloudFileStore";
import { CommandStore } from "./store/CommandStore";
import { CommandTemplateStore } from "./store/CommandTemplateStore";
import { CommentStore } from "./store/CommentStore";
import { ConnectedStore } from "./store/ConnectedStore";
import { CountStore } from "./store/CountStore";
import { DeviceConfigurationStore } from "./store/DeviceConfigurationStore";
import { DeviceConfigurationTemplateStore } from "./store/DeviceConfigurationTemplateStore";
import { DeviceDetailsStore } from "./store/DeviceDetailsStore";
import { DeviceStore } from "./store/DeviceStore";
import { DocumentSetStore } from "./store/DocumentSetStore";
import { EventStore } from "./store/EventStore";
import { EventTriggerStore } from "./store/EventTriggerStore";
import { FtueStore } from "./store/FtueStore";
import { GroupStore } from "./store/GroupStore";
import { JumpStore } from "./store/JumpStore";
import { MetadataStore } from "./store/MetadataStore";
import { ModuleStore } from "./store/ModuleStore";
import { ObserveStore } from "./store/ObserveStore";
import { OnDemandStore } from "./store/OnDemandStore";
import { OnlineDevicesStore } from "./store/OnlineDevicesStore";
import { OperateStore } from "./store/OperateStore";
import { OrganizationStore } from "./store/OrganizationStore";
import { PresenceStore } from "./store/PresenceStore";
import { QueryStore } from "./store/QueryStore";
import { RoleStore } from "./store/RoleStore";
import { RouterStore } from "./store/RouterStore";
import { RtcSignalingStore } from "./store/RtcSignalingStore";
import { ScheduleStore } from "./store/ScheduleStore";
import { ScreenRecordingStore } from "./store/ScreenRecordingStore";
import { ShareStore } from "./store/ShareStore";
import { SignalingStore } from "./store/SignalingStore";
import { SsoStore } from "./store/SsoStore";
import { StreamCurrentValueStore } from "./store/StreamCurrentValueStore";
import { StreamStore } from "./store/StreamStore";
import { TagTemplateStore } from "./store/TagTemplateStore";
import { TeamStore } from "./store/TeamStore";
import { TeleopSessionStore } from "./store/TeleopSessionStore";
import { UsageMetricsStore } from "./store/UsageMetricsStore";
import { UserStore } from "./store/UserStore";
import { ViewStore } from "./store/ViewStore";

export const authStore = new AuthStore(
    authClient,
    organizationClient,
    roleClient,
    shareClient,
    userClient,
);

export const organizationStore = new OrganizationStore(
    organizationClient,
    authStore,
);

export const eventTriggerStore = new EventTriggerStore(
    eventTriggerClient,
    authStore,
);

export const channelStore = new ChannelStore(channelClient, authStore);

export const commandStore: ICommandStore = new CommandStore(
    commandClient,
    authStore,
);

export const onlineDevicesStore = new OnlineDevicesStore(
    onlineDevicesClient,
    authStore,
);

export const operateStore = new OperateStore();

export const appStore = new AppStore(operateStore);

export const deviceDetailsStore = new DeviceDetailsStore(
    deviceDetailsClient,
    authStore,
    appStore,
    onlineDevicesStore,
);

export const viewStore = new ViewStore(
    viewClient,
    authStore,
    deviceDetailsStore,
);

export const deviceStore: IDeviceStore = new DeviceStore(
    deviceClient,
    authStore,
    commandStore,
    deviceDetailsStore,
);

export const deviceConfigurationStore = new DeviceConfigurationStore(
    deviceClient,
    authStore,
    deviceStore,
);

export const eventStore: IEventStore = new EventStore(eventClient, authStore);

export const presenceStore = new PresenceStore(presenceClient, authStore);

export const metadataStore = new MetadataStore(
    metadataClient,
    authStore,
    deviceDetailsStore,
);

export const scheduleStore = new ScheduleStore(scheduleClient, authStore);

export const usageMetricsStore = new UsageMetricsStore(
    usageMetricsClient,
    authStore,
);

export const ssoStore = new SsoStore(ssoClient, authStore);

export const onDemandStore = new OnDemandStore(
    commandStore,
    deviceConfigurationStore,
    deviceStore,
    eventStore,
);

export const shareStore = new ShareStore(shareClient, authStore);

// V1
export const rtcSignalingStore = new RtcSignalingStore(
    rtcSignalingClient,
    authStore,
    deviceDetailsStore,
);

// V2
export const signalingStore = new SignalingStore(
    signalingV2Client,
    authStore,
    deviceDetailsStore,
);

export const roleStore = new RoleStore(roleClient, authStore);

export const userStore = new UserStore(userClient, authStore);

export const teamStore = new TeamStore(teamClient, authStore, userStore);

export const routerStore = new RouterStore(operateStore, logger);

export const observeStore = new ObserveStore({
    appStore,
    authStore,
    eventStore,
    routerStore,
    organizationStore,
});

export const queryStore: IQueryStore = new QueryStore(
    queryClient,
    authStore,
    appStore,
    deviceDetailsStore,
    observeStore,
    onDemandStore,
);

export const deviceConfigurationTemplateStore =
    new DeviceConfigurationTemplateStore(
        deviceConfigurationTemplateClient,
        authStore,
    );

export const commandTemplateStore = new CommandTemplateStore(
    commandTemplateClient,
    authStore,
);

export const commentStore = new CommentStore(
    commentClient,
    authStore,
    eventStore,
);

export const countStore = new CountStore(countClient, authStore);

export const annotationStore = new AnnotationStore(
    annotationClient,
    authStore,
    eventStore,
);

export const annotationTemplateStore = new AnnotationTemplateStore(
    annotationTemplateClient,
    authStore,
    organizationStore,
);

export const groupStore = new GroupStore(groupClient, authStore);

export const jumpStore = new JumpStore({
    authStore,
    channelStore,
    deviceDetailsStore,
    groupStore,
    routerStore,
    rtcSignalingStore,
    signalingStore,
});

export const tagTemplateStore = new TagTemplateStore(
    tagTemplateClient,
    authStore,
);

export const teleopSessionStore = new TeleopSessionStore(
    appStore,
    authStore,
    deviceConfigurationStore,
    deviceDetailsStore,
    deviceStore,
    organizationStore,
    routerStore,
    rtcSignalingStore,
    signalingStore,
);

export const screenRecordingStore = new ScreenRecordingStore(appStore);

export const streamStore = new StreamStore(streamClient, authStore);

export const streamCurrentValueStore = new StreamCurrentValueStore(
    streamCurrentValueClient,
    authStore,
);

export const ftueStore = new FtueStore();

export const documentSetStore = new DocumentSetStore(
    documentRagServiceClient,
    authStore,
);

export const benchmarkStore = new BenchmarkStore();

export const adapterStore = new AdapterStore(adapterClient, authStore);

export const customModuleStore: IModuleStore = new ModuleStore(
    moduleClient,
    moduleConfigurationClient,
    authStore,
);

export const cloudFileStore = new CloudFileStore(fileClient, authStore);

export const analyticsModuleStore = new AnalyticsModuleStore(
    analyticsModuleClient,
    authStore,
);

export const analyticsStore = new AnalyticsStore(
    analyticsClient,
    appStore,
    authStore,
);

export const connectedStore = new ConnectedStore([
    adminApi,
    queryApi,
    ingestionApi,
]);
